
import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { LanguageCode } from '@language';
import { TranslateModule } from '@ngx-translate/core';
import {
    ApplicationConfigService,
    Banner,
    BannerContent,
    BaseContainer,
    LocalStorageID,
    ShNotificationComponent
} from '@stylehaven/components';
import { map, shareReplay } from 'rxjs/operators';

@Component({
    standalone: true,
    selector: 'med-banner',
    templateUrl: './banner-container.component.html',
    imports: [TranslateModule, ShNotificationComponent]
})
export class BannerContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly LOCAL_STORAGE_ID: string = LocalStorageID.Banner;

    public showBanner: boolean;
    public title: string;
    public message: string;
    public canBeClosed: boolean;

    @HostBinding('class.remove-top-padding') get isVisible() {
        return this.showBanner;
    }

    constructor(private readonly configService: ApplicationConfigService) {
        super();
    }

    ngOnInit(): void {
        this.initBannerSettings();
    }

    private initBannerSettings(): void {
        this.configService
            .getConfig()
            .pipe(
                map(config => config.banner),
                shareReplay(1)
            )
            .subscribe(banner => this.setBannerProperties(banner));
    }

    private setBannerProperties(banner: Banner): void {
        const content = this.getBannerContent(banner);

        if (content) {
            this.setBannerTitle(content);
            this.setBannerMessage(content);
        }

        this.setBannerClosability(banner);
        this.setBannerVisibility(banner);
    }

    private setBannerTitle(bannerContent: BannerContent): void {
        this.title = bannerContent.title;
    }

    private setBannerMessage(bannerContent: BannerContent): void {
        this.message = bannerContent.paragraphs.length ? bannerContent.paragraphs.join('\n') : '';
    }

    private setBannerClosability(banner: Banner): void {
        this.canBeClosed = banner.closable ?? false;
    }

    private setBannerVisibility(banner: Banner): void {
        if (!banner.enabled) {
            this.showBanner = false;
            return;
        }

        // Always show when banner not closable, even when user closed it before.
        if (!banner.closable) {
            this.showBanner = true;
            return;
        }

        if (!this.hasClosedBanner()) {
            this.showBanner = true;
            return;
        }
    }

    private getBannerContent(banner: Banner): BannerContent | null {
        const currentLang: LanguageCode = this.translateService.getCurrentLanguage();
        const content = banner.content?.[currentLang] || null;

        if (!content) {
            console.error('BannerContainerComponent:: No banner content translation found');
        }

        return content;
    }

    private hasClosedBanner(): boolean {
        const closedBanner = localStorage.getItem(this.LOCAL_STORAGE_ID);
        return !!closedBanner;
    }

    public onClose(): void {
        try {
            localStorage.setItem(this.LOCAL_STORAGE_ID, 'true');
            this.showBanner = false;
        } catch (error) {
            console.error('Failed to set item in localStorage:', error);
        }
    }
}
